
import { PlannedOrder, OrderType } from '@/orderplan/orderplan';
import {
  bigNumberToFixed,
  compareBignumber,
  fixedToBigNumber,
  formatMaxDigits,
  numberMixin,
  toleranceMin,
} from '@/util/numbers';
import { getTokenBalance } from '@/util/tokens';
import { idleService } from '@/web3/idleService';
import { ParaSwapPredictedOutput, paraswapService, PredictedOutput } from '@/web3/paraswapService';
import { TransactionResult, uniswapService } from '@/web3/uniswapService';
import { extractErrorMessage, web3Service } from '@/web3/web3Service';
import { defineComponent, PropType, reactive, ref, Ref, watchEffect } from 'vue';

interface TransactionInProgress {
  inProgress?: boolean;
}
interface Option {
  platform: string;
  inProgress: boolean;
  message?: string;
  plan?: PredictedOutput;
}
interface OrderOptions {
  options?: Option[];
  chosenPlatform?: string;
}
type OrderWithResult = PlannedOrder &
  Partial<TransactionResult> &
  TransactionInProgress &
  OrderOptions;

export default defineComponent({
  name: 'OrderPlanDialog',
  props: {
    visible: Boolean,
    orderPlan: { type: Object as PropType<PlannedOrder[]>, required: true },
    isEnzyme: Boolean,
  },
  emits: ['update:visible'],
  setup(props) {
    const orderList: Ref<OrderWithResult[]> = ref([]);
    watchEffect(() => {
      orderList.value = props.orderPlan.map((order) => ({
        ...order,
        chosenPlatform: order.ordertype == OrderType.SWAP ? undefined : 'idle',
      }));
      console.log(props.orderPlan, orderList.value);
    });
    return { orderList };
  },
  methods: {
    async calculateBest(order: OrderWithResult) {
      const address = web3Service.status().address;
      if (!address) {
        throw new Error('web3 not initialized yet');
      }
      const uniswapOption: Option = reactive({
        platform: 'Uniswap',
        inProgress: true,
      });
      const paraswapOption: Option = reactive({
        platform: 'Paraswap',
        inProgress: true,
      });
      order.options = [uniswapOption, paraswapOption];
      await this.prepareOrder(order, address);
      const uniswapOutput = uniswapService.getPredictedOutput(order).then((output) => {
        console.log('output uniswap: ', output);
        uniswapOption.inProgress = false;
        uniswapOption.plan = output;
      });
      const paraswapOutput = paraswapService.getPredictedOutput(order).then((output) => {
        console.log('output paraswap: ', output);
        paraswapOption.inProgress = false;
        paraswapOption.plan = output;
      });
      await Promise.all([uniswapOutput, paraswapOutput]);
      order.options.sort((a, b) => {
        if (a.plan !== undefined && b.plan !== undefined) {
          return -compareBignumber(a.plan.predictedOutput, b.plan.predictedOutput);
        } else if (a.plan === undefined) {
          if (b.plan === undefined) {
            return 0;
          } else {
            return 1;
          }
        } else {
          return -1;
        }
      });
    },
    async execute(order: OrderWithResult, platform?: string, plan?: PredictedOutput) {
      order.inProgress = true;
      order.message = undefined;
      try {
        const address = web3Service.status().address;
        if (!address) {
          throw new Error('connect wallet first');
        }

        let executeFunction: (arg: PlannedOrder) => Promise<TransactionResult>;
        if (this.$props.isEnzyme) {
          executeFunction = (order) => uniswapService.executeForEnzyme(order);
        } else if (order.ordertype == OrderType.DEPOSIT) {
          executeFunction = (order) =>
            idleService.depositToken(order.fromToken, address, order.sendAmount);
        } else if (order.ordertype == OrderType.REDEEM) {
          executeFunction = (order) =>
            idleService.redeemToken(order.fromToken, address, order.sendAmount);
        } else {
          await this.prepareOrder(order, address);
          if (platform === undefined) {
            // work on a copy so we keep the UI clean
            const copy = { ...order };
            await this.calculateBest(copy);
            if (!copy.options || !copy.options[0]?.plan) {
              throw new Error('No suitable option');
            }
            platform = copy.options[0].platform;
            plan = copy.options[0].plan;
            console.log(`${platform} selected as best: ${plan.predictedOutput.toString()}`);
          }
          switch (platform) {
            case 'Uniswap':
              executeFunction = (order) => uniswapService.execute(order);
              break;
            case 'Paraswap':
              executeFunction = (order) =>
                paraswapService.execute(order, plan as ParaSwapPredictedOutput);
              break;
            default:
              throw new Error('Unknown platform: ' + platform);
          }
          order.chosenPlatform = platform;
        }
        const result = await executeFunction(order);
        Object.assign(order, result);
      } catch (error) {
        console.log(error);
        let message = extractErrorMessage(error);
        order.success = false;
        order.message = message;
      } finally {
        order.inProgress = false;
      }
    },
    platformLogo(platform: string): string {
      return require('@/assets/' + platform.toLowerCase() + '.png');
    },
    formatResult(order: PlannedOrder, option: Option): string {
      const result = option.plan?.predictedOutput;
      if (result === undefined) {
        return '---';
      }
      const fixed = bigNumberToFixed(result, order.toToken.decimals);
      return order.toToken.symbol + ' ' + formatMaxDigits(fixed.toUnsafeFloat());
    },
    isSwap(order: PlannedOrder): boolean {
      return order.ordertype == OrderType.SWAP;
    },
    executeButtonText(order: PlannedOrder): string {
      if (order.ordertype == OrderType.SWAP && !this.isEnzyme) {
        return 'Execute best';
      } else {
        return 'Execute';
      }
    },
    fromName(order: PlannedOrder): string {
      const name = order.fromToken.name;
      if (order.ordertype == OrderType.REDEEM) {
        return 'Idle' + order.fromToken.symbol;
      } else {
        return name;
      }
    },
    toName(order: PlannedOrder): string {
      const name = order.toToken.name;
      if (order.ordertype == OrderType.DEPOSIT) {
        return 'Idle' + order.fromToken.symbol;
      } else {
        return name;
      }
    },
    async prepareOrder(order: PlannedOrder, address: string) {
      // Make sure that the amount wanted is not higher than our balance
      const balance = await getTokenBalance(order.fromToken.id, address);
      const amountBn = fixedToBigNumber(order.sendAmount, order.fromToken.decimals);
      const newAmount = toleranceMin(amountBn, balance);
      order.sendAmount = bigNumberToFixed(newAmount, order.fromToken.decimals);
    },
  },
  mixins: [numberMixin],
});
